'use client';

import React, { useState, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Pill, 
  Clock, 
  AlertCircle,
  CheckCircle2,
  Filter,
  Search,
  MoreVertical,
  Edit2,
  Trash2,
  Bell,
  BellOff,
  Archive,
  RefreshCw
} from 'lucide-react';
import { useMedications, useUpdateMedication, useDeleteMedication } from '@/features/medications/hooks/useMedications';
import { useChildren } from '@/features/children/hooks/useChildren';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { PageHeader } from '@/components/layout/PageHeader';
import { ChildSelector } from '@/components/shared/ChildSelector';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DropdownMenu, DropdownMenuItem } from '@/components/ui/DropdownMenu';
import { MedicationFormModal } from '@/features/medications/components/MedicationFormModal';
import { useToast } from '@/core/hooks/useToast';
import { formatTime, formatDate } from '@/utils/dateUtils';
import type { Medication } from '@/features/health/types/healthTypes';

type ViewFilter = 'active' | 'inactive' | 'all';

export function MedicationsPage(): React.ReactElement {
  const navigate = useNavigate();
  const { familyId } = useParams<{ familyId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [viewFilter, setViewFilter] = useState<ViewFilter>('active');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingMedication, setEditingMedication] = useState<Medication | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Medication | null>(null);

  // URL params
  const selectedChildId = searchParams.get('childId') || '';

  // Data fetching
  const { data: children, isLoading: childrenLoading } = useChildren(familyId!);
  const { 
    data: medications, 
    isLoading: medicationsLoading,
    refetch,
  } = useMedications(selectedChildId, {
    activeOnly: viewFilter === 'active',
    inactiveOnly: viewFilter === 'inactive',
  });

  const updateMedication = useUpdateMedication();
  const deleteMedication = useDeleteMedication();

  // Auto-select first child
  React.useEffect(() => {
    if (children && children.length > 0 && !selectedChildId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('childId', children[0].id);
      setSearchParams(newParams);
    }
  }, [children, selectedChildId, searchParams, setSearchParams]);

  const selectedChild = useMemo(() => {
    return children?.find((c) => c.id === selectedChildId);
  }, [children, selectedChildId]);

  // Filter medications by search
  const filteredMedications = useMemo(() => {
    if (!medications) return [];
    if (!searchQuery) return medications;
    
    const query = searchQuery.toLowerCase();
    return medications.filter((med: Medication) => 
      med.name.toLowerCase().includes(query) ||
      med.genericName?.toLowerCase().includes(query) ||
      med.prescribingDoctor?.toLowerCase().includes(query)
    );
  }, [medications, searchQuery]);

  // Handlers
  const handleChildChange = (childId: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (childId) {
      newParams.set('childId', childId);
    } else {
      newParams.delete('childId');
    }
    setSearchParams(newParams);
  };

  const handleAddMedication = () => {
    setEditingMedication(null);
    setShowFormModal(true);
  };

  const handleEditMedication = (medication: Medication) => {
    setEditingMedication(medication);
    setShowFormModal(true);
  };

  const handleToggleActive = async (medication: Medication) => {
    try {
      await updateMedication.mutateAsync({
        id: medication.id,
        isActive: !medication.isActive,
      });
      showToast({
        type: 'success',
        title: medication.isActive ? 'Medication archived' : 'Medication reactivated',
        message: `${medication.name} has been ${medication.isActive ? 'archived' : 'reactivated'}.`,
      });
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Update failed',
        message: 'Failed to update medication status.',
      });
    }
  };

  const handleToggleReminder = async (medication: Medication) => {
    try {
      await updateMedication.mutateAsync({
        id: medication.id,
        reminderEnabled: !medication.reminderEnabled,
      });
      showToast({
        type: 'success',
        title: medication.reminderEnabled ? 'Reminders disabled' : 'Reminders enabled',
        message: `Reminders have been ${medication.reminderEnabled ? 'disabled' : 'enabled'} for ${medication.name}.`,
      });
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Update failed',
        message: 'Failed to update reminder settings.',
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteMedication.mutateAsync(deleteTarget.id);
      showToast({
        type: 'success',
        title: 'Medication deleted',
        message: `${deleteTarget.name} has been removed.`,
      });
      setDeleteTarget(null);
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Delete failed',
        message: 'Failed to delete medication.',
      });
    }
  };

  const handleFormSuccess = () => {
    setShowFormModal(false);
    setEditingMedication(null);
    refetch();
  };

  const isLoading = childrenLoading || medicationsLoading;

  if (childrenLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!children || children.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          icon={<Pill className="w-12 h-12 text-warm-gray" />}
          title="No children added yet"
          description="Add a child to your family to start tracking their medications."
          action={
            <Button onClick={() => navigate(`/family/${familyId}/children/new`)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Child
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Page Header */}
      <PageHeader
        title="Medications"
        description={selectedChild ? `Managing medications for ${selectedChild.first_name}` : 'Medication management'}
        breadcrumbs={[
          { label: 'Health', href: `/family/${familyId}/health` },
          { label: 'Medications' },
        ]}
        actions={
          <Button onClick={handleAddMedication}>
            <Plus className="w-4 h-4 mr-2" />
            Add Medication
          </Button>
        }
      />

      {/* Child Selector */}
      {children.length > 1 && (
        <div className="mb-6">
          <ChildSelector
            children={children}
            selectedChildId={selectedChildId}
            onSelect={handleChildChange}
          />
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
          <Input
            type="text"
            placeholder="Search medications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* View Filter */}
        <div className="flex rounded-lg border border-warm-gray/20 overflow-hidden">
          {(['active', 'inactive', 'all'] as ViewFilter[]).map((filter) => (
            <button
              key={filter}
              onClick={() => setViewFilter(filter)}
              className={`
                px-4 py-2 text-sm font-medium capitalize
                ${viewFilter === filter 
                  ? 'bg-sage text-white' 
                  : 'bg-white text-warm-gray hover:bg-cream'
                }
              `}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      {/* Medications List */}
      {medicationsLoading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      ) : !filteredMedications.length ? (
        <EmptyState
          icon={<Pill className="w-12 h-12 text-warm-gray" />}
          title={searchQuery ? "No medications found" : viewFilter === 'active' ? "No active medications" : "No medications added"}
          description={
            searchQuery 
              ? "Try adjusting your search terms."
              : viewFilter === 'active'
              ? "Add a medication to start tracking."
              : "All medications have been archived."
          }
          action={
            !searchQuery && viewFilter === 'active' ? (
              <Button onClick={handleAddMedication}>
                <Plus className="w-4 h-4 mr-2" />
                Add Medication
              </Button>
            ) : viewFilter !== 'active' ? (
              <Button variant="secondary" onClick={() => setViewFilter('active')}>
                View Active Medications
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-4">
          {filteredMedications.map((medication: Medication) => (
            <MedicationCard
              key={medication.id}
              medication={medication}
              onEdit={() => handleEditMedication(medication)}
              onDelete={() => setDeleteTarget(medication)}
              onToggleActive={() => handleToggleActive(medication)}
              onToggleReminder={() => handleToggleReminder(medication)}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showFormModal && (
        <MedicationFormModal
          childId={selectedChildId}
          medication={editingMedication}
          onClose={() => {
            setShowFormModal(false);
            setEditingMedication(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Medication"
        description={`Are you sure you want to delete ${deleteTarget?.name}? This will also remove all associated reminders.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        isLoading={deleteMedication.isPending}
      />
    </div>
  );
}

// Medication Card Component
interface MedicationCardProps {
  medication: Medication;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  onToggleReminder: () => void;
}

function MedicationCard({
  medication,
  onEdit,
  onDelete,
  onToggleActive,
  onToggleReminder,
}: MedicationCardProps): React.ReactElement {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <Card className={`p-6 ${!medication.isActive ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className={`
            w-12 h-12 rounded-xl flex items-center justify-center
            ${medication.isActive ? 'bg-blue-50 text-blue-500' : 'bg-gray-100 text-gray-400'}
          `}>
            <Pill className="w-6 h-6" />
          </div>
          
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-display font-semibold text-navy">
                {medication.name}
              </h3>
              {!medication.isActive && (
                <Badge variant="secondary">Archived</Badge>
              )}
              {medication.reminderEnabled && medication.isActive && (
                <Badge variant="success" className="flex items-center gap-1">
                  <Bell className="w-3 h-3" />
                  Reminders On
                </Badge>
              )}
            </div>
            
            {medication.genericName && (
              <p className="text-sm text-warm-gray">
                Generic: {medication.genericName}
              </p>
            )}
            
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-warm-gray">
              <span className="font-medium text-navy">
                {medication.dosage}
              </span>
              <span>
                {medication.frequency}
              </span>
              {medication.route && (
                <span className="capitalize">
                  {medication.route}
                </span>
              )}
            </div>

            {medication.reminderTimes && medication.reminderTimes.length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-warm-gray" />
                <span className="text-warm-gray">
                  {medication.reminderTimes.map((time) => formatTime(time)).join(', ')}
                </span>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-warm-gray">
              {medication.prescribingDoctor && (
                <span>Prescribed by: {medication.prescribingDoctor}</span>
              )}
              {medication.pharmacy && (
                <span>Pharmacy: {medication.pharmacy}</span>
              )}
              {medication.startDate && (
                <span>Started: {formatDate(medication.startDate)}</span>
              )}
            </div>
          </div>
        </div>

        <DropdownMenu
          isOpen={showMenu}
          onToggle={() => setShowMenu(!showMenu)}
          onClose={() => setShowMenu(false)}
          trigger={
            <button className="p-2 hover:bg-cream rounded-lg transition-colors">
              <MoreVertical className="w-5 h-5 text-warm-gray" />
            </button>
          }
        >
          <DropdownMenuItem onClick={onEdit}>
            <Edit2 className="w-4 h-4 mr-2" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onToggleReminder}>
            {medication.reminderEnabled ? (
              <>
                <BellOff className="w-4 h-4 mr-2" />
                Disable Reminders
              </>
            ) : (
              <>
                <Bell className="w-4 h-4 mr-2" />
                Enable Reminders
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onToggleActive}>
            {medication.isActive ? (
              <>
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reactivate
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDelete} className="text-red-500">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenu>
      </div>

      {medication.instructions && (
        <div className="mt-4 p-3 bg-cream rounded-lg">
          <p className="text-sm text-navy">
            <strong className="text-warm-gray">Instructions:</strong> {medication.instructions}
          </p>
        </div>
      )}
    </Card>
  );
}

export default MedicationsPage;


